import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { 
  Send, Crown, User, Bot, RefreshCw, Lightbulb, TrendingUp, Users, Target,
  ArrowRight, Award, AlertTriangle, BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { useAuth } from "../App";
import { stripMarkdown, stripMarkdownObj } from "../utils/stripMarkdown";
import { CopyButton } from "../components/CopyButton";

const ManagementCopilotPage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I'm your AI Management Copilot. I can help you with strategic insights like:\n\n- Pipeline health and forecasting\n- Team performance analysis\n- Campaign effectiveness\n- Risk identification\n- Strategic recommendations\n\nWhat would you like to know?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [contextStats, setContextStats] = useState(null);
  const [repMatching, setRepMatching] = useState(null);
  const [repMatchLoading, setRepMatchLoading] = useState(false);
  const [benchmarks, setBenchmarks] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/management-copilot`, {
        query: userMessage
      });
      
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: stripMarkdown(response.data.response) 
      }]);
      setContextStats(response.data.context_stats);
    } catch (error) {
      console.error("Management copilot error:", error);
      if (error.response?.status === 403) {
        toast.error("Management access required");
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "Sorry, you need admin or sales head access to use the Management Copilot." 
        }]);
      } else {
        toast.error("Failed to get response");
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "I'm sorry, I encountered an error. Please try again." 
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const fetchRepMatching = async () => {
    setRepMatchLoading(true);
    try {
      const res = await axios.post(`${API}/ai/rep-lead-matching`);
      setRepMatching(stripMarkdownObj(res.data));
    } catch (error) {
      console.error("Rep matching error:", error);
      toast.error("Failed to load rep-lead matching");
    } finally {
      setRepMatchLoading(false);
    }
  };

  const fetchBenchmarks = async () => {
    setBenchmarkLoading(true);
    try {
      const res = await axios.get(`${API}/ai/performance-benchmarks`);
      setBenchmarks(stripMarkdownObj(res.data));
    } catch (error) {
      console.error("Benchmarks error:", error);
      toast.error("Failed to load performance benchmarks");
    } finally {
      setBenchmarkLoading(false);
    }
  };

  const suggestedQueries = [
    "What's our pipeline health this month?",
    "Which lead sources are underperforming?",
    "Show me team performance insights",
    "What are the top risks in our funnel?",
    "Forecast next month's bookings",
    "Which campaigns should we double down on?"
  ];

  const MANAGEMENT_ROLES = ['admin', 'ceo', 'hod_sales', 'sales_head'];
  const hasAccess = MANAGEMENT_ROLES.includes(user?.role);

  if (!hasAccess) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Card className="bg-white border-slate-200 max-w-md">
            <CardContent className="p-8 text-center">
              <Crown className="w-12 h-12 mx-auto text-amber-500 mb-4" />
              <h2 className="text-xl font-bold text-slate-900 font-['Manrope'] mb-2">Management Access Required</h2>
              <p className="text-slate-600">The Management Copilot is available for Admins, CEO, HOD Sales, and Sales Heads only.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="management-copilot-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Management Copilot</h1>
            <p className="text-slate-500">Strategic insights for leadership</p>
          </div>
        </div>

        {/* Stats Cards */}
        {contextStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <div className="bg-slate-100 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-slate-900">{contextStats.total_leads}</p>
              <p className="text-xs text-slate-600">Total Leads</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{contextStats.hot_leads}</p>
              <p className="text-xs text-slate-600">Hot Leads</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{contextStats.conversion_rate}%</p>
              <p className="text-xs text-slate-600">Conversion</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{contextStats.pending_tasks}</p>
              <p className="text-xs text-slate-600">Pending Tasks</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{contextStats.total_bookings}</p>
              <p className="text-xs text-slate-600">Bookings</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{contextStats.booking_value ? `${(contextStats.booking_value / 100000).toFixed(0)}L` : '0'}</p>
              <p className="text-xs text-slate-600">Booking Value</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-purple-600">{contextStats.active_eois}</p>
              <p className="text-xs text-slate-600">Active EOIs</p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-indigo-600">{contextStats.team_size}</p>
              <p className="text-xs text-slate-600">Team Size</p>
            </div>
          </div>
        )}

        {/* Tabbed Content */}
        <Tabs defaultValue="chat" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="chat" data-testid="copilot-chat-tab">
              <Crown className="w-4 h-4 mr-2" /> Copilot
            </TabsTrigger>
            <TabsTrigger value="rep-matching" data-testid="rep-matching-tab" onClick={() => !repMatching && fetchRepMatching()}>
              <Users className="w-4 h-4 mr-2" /> Rep Matching
            </TabsTrigger>
            <TabsTrigger value="benchmarks" data-testid="benchmarks-tab" onClick={() => !benchmarks && fetchBenchmarks()}>
              <Award className="w-4 h-4 mr-2" /> Benchmarks
            </TabsTrigger>
          </TabsList>

          {/* Chat Tab */}
          <TabsContent value="chat" className="mt-4">
            <Card className="bg-white border-slate-200 shadow-sm overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 22rem)' }}>
              <CardContent className="flex-1 p-4 overflow-y-auto">
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.role === 'user' ? 'bg-slate-900' : 'bg-purple-100'
                      }`}>
                        {msg.role === 'user' ? (
                          <User className="w-4 h-4 text-white" />
                        ) : (
                          <Crown className="w-4 h-4 text-purple-600" />
                        )}
                      </div>
                      <div className={`max-w-[80%] p-4 rounded-2xl ${
                        msg.role === 'user' 
                          ? 'bg-slate-900 text-white' 
                          : 'bg-purple-50 text-slate-900'
                      }`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        {msg.role === 'assistant' && idx > 0 && (
                          <div className="mt-2 flex justify-end">
                            <CopyButton text={msg.content} label={`chat-${idx}`} />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {loading && (
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <Crown className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="bg-purple-50 p-4 rounded-2xl">
                        <div className="flex items-center gap-2">
                          <RefreshCw className="w-4 h-4 animate-spin text-purple-500" />
                          <span className="text-purple-600">Analyzing...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </CardContent>

              {/* Suggestions */}
              {messages.length <= 1 && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-slate-500 mb-2 flex items-center gap-1">
                    <Lightbulb className="w-4 h-4" />
                    Strategic questions:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedQueries.map((query, idx) => (
                      <button
                        key={idx}
                        className="text-sm px-3 py-1.5 bg-purple-100 hover:bg-purple-200 rounded-full text-purple-700 transition-colors"
                        onClick={() => setInput(query)}
                        data-testid={`suggestion-${idx}`}
                      >
                        {query}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="p-4 border-t border-slate-200">
                <div className="flex gap-2">
                  <Input
                    data-testid="mgmt-copilot-input"
                    placeholder="Ask about pipeline, performance, forecasts..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                    className="flex-1"
                  />
                  <Button 
                    data-testid="mgmt-copilot-send-btn"
                    onClick={handleSend} 
                    disabled={!input.trim() || loading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Rep-Lead Matching Tab */}
          <TabsContent value="rep-matching" className="mt-4 space-y-6">
            {repMatchLoading && !repMatching ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center text-slate-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-purple-500" />
                  Optimizing rep-lead assignments...
                </CardContent>
              </Card>
            ) : repMatching ? (
              <>
                {/* Optimization score */}
                <div className="flex items-center gap-4">
                  <span className={`inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-full ${
                    repMatching.optimization_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                    repMatching.optimization_score >= 40 ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`} data-testid="optimization-score">
                    Optimization Score: {repMatching.optimization_score}/100
                  </span>
                  <Button variant="ghost" size="sm" onClick={fetchRepMatching} disabled={repMatchLoading}>
                    <RefreshCw className={`w-4 h-4 ${repMatchLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>

                {/* Rep Strengths */}
                {repMatching.rep_strengths?.length > 0 && (
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold font-['Manrope']">Rep Strengths</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {repMatching.rep_strengths.map((rep, i) => (
                          <div key={i} className="p-4 bg-purple-50 rounded-xl border border-purple-100" data-testid={`rep-strength-${i}`}>
                            <p className="font-semibold text-slate-900">{rep.rep_name}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {rep.strengths?.map((s, j) => (
                                <span key={j} className="text-xs px-2 py-1 bg-white rounded-full text-purple-700 border border-purple-200">{s}</span>
                              ))}
                            </div>
                            <p className="text-sm text-slate-600 mt-2">Best for: <span className="font-medium">{rep.best_for}</span></p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recommendations */}
                {repMatching.recommendations?.length > 0 && (
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold font-['Manrope']">Reassignment Recommendations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {repMatching.recommendations.map((rec, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg" data-testid={`rep-rec-${i}`}>
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{rec.lead_name}</p>
                              <p className="text-sm text-slate-500">{rec.reason}</p>
                            </div>
                            <div className="flex items-center gap-2 text-sm shrink-0">
                              <span className="text-red-600">{rec.current_rep}</span>
                              <ArrowRight className="w-4 h-4 text-slate-400" />
                              <span className="text-emerald-600 font-medium">{rec.recommended_rep}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Rep Stats Table */}
                {repMatching.rep_stats?.length > 0 && (
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold font-['Manrope']">Team Overview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-2 px-3 text-slate-500 font-medium">Rep</th>
                              <th className="text-center py-2 px-3 text-slate-500 font-medium">Leads</th>
                              <th className="text-center py-2 px-3 text-slate-500 font-medium">Booked</th>
                              <th className="text-center py-2 px-3 text-slate-500 font-medium">Conv %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {repMatching.rep_stats.map((rep, i) => (
                              <tr key={i} className="border-b border-slate-100">
                                <td className="py-2 px-3 font-medium text-slate-900">{rep.name}</td>
                                <td className="py-2 px-3 text-center">{rep.total_leads}</td>
                                <td className="py-2 px-3 text-center text-emerald-600">{rep.booked}</td>
                                <td className="py-2 px-3 text-center">
                                  <span className={`font-medium ${rep.conversion_rate > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                                    {rep.conversion_rate}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center">
                  <Button onClick={fetchRepMatching} disabled={repMatchLoading} data-testid="load-rep-matching-btn">
                    <Users className="w-4 h-4 mr-2" /> Analyze Rep-Lead Matching
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Performance Benchmarks Tab */}
          <TabsContent value="benchmarks" className="mt-4 space-y-6">
            {benchmarkLoading && !benchmarks ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center text-slate-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-3 text-purple-500" />
                  Analyzing team performance...
                </CardContent>
              </Card>
            ) : benchmarks ? (
              <>
                {/* Overall rating + top performer */}
                <div className="flex flex-wrap items-center gap-4">
                  <span className={`inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-full ${
                    benchmarks.overall_rating === 'excellent' ? 'bg-emerald-100 text-emerald-700' :
                    benchmarks.overall_rating === 'good' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`} data-testid="overall-rating">
                    Team: {benchmarks.overall_rating?.replace(/_/g, ' ')}
                  </span>
                  {benchmarks.top_performer && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-full bg-amber-100 text-amber-700" data-testid="top-performer">
                      <Award className="w-4 h-4" /> Top: {benchmarks.top_performer}
                    </span>
                  )}
                  <Button variant="ghost" size="sm" onClick={fetchBenchmarks} disabled={benchmarkLoading}>
                    <RefreshCw className={`w-4 h-4 ${benchmarkLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>

                {/* Benchmarks Table */}
                {benchmarks.benchmarks?.length > 0 && (
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold font-['Manrope']">Performance Benchmarks</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-2 px-3 text-slate-500 font-medium">Rep</th>
                              <th className="text-center py-2 px-3 text-slate-500 font-medium">Leads</th>
                              <th className="text-center py-2 px-3 text-slate-500 font-medium">Conv %</th>
                              <th className="text-center py-2 px-3 text-slate-500 font-medium">Loss %</th>
                              <th className="text-center py-2 px-3 text-slate-500 font-medium">Tasks Done</th>
                              <th className="text-center py-2 px-3 text-slate-500 font-medium">Activity/wk</th>
                            </tr>
                          </thead>
                          <tbody>
                            {benchmarks.benchmarks.map((b, i) => (
                              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-medium text-slate-900">{b.name}</td>
                                <td className="py-2 px-3 text-center">{b.total_leads}</td>
                                <td className="py-2 px-3 text-center">
                                  <span className={b.conversion_rate > 0 ? 'text-emerald-600 font-medium' : 'text-slate-400'}>{b.conversion_rate}%</span>
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <span className={b.loss_rate > 30 ? 'text-red-600 font-medium' : 'text-slate-600'}>{b.loss_rate}%</span>
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <span className={b.task_completion > 70 ? 'text-emerald-600' : b.task_completion > 0 ? 'text-amber-600' : 'text-slate-400'}>{b.task_completion}%</span>
                                </td>
                                <td className="py-2 px-3 text-center">{b.weekly_interactions}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Coaching Needed */}
                {benchmarks.coaching_needed?.length > 0 && (
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold font-['Manrope']">Coaching Recommendations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {benchmarks.coaching_needed.map((c, i) => (
                          <div key={i} className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid={`coaching-${i}`}>
                            <p className="font-semibold text-slate-900">{c.rep_name}</p>
                            <p className="text-sm text-amber-700 mt-1">Area: {c.area}</p>
                            <p className="text-sm text-slate-600 mt-1">{c.suggestion}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Team Insights */}
                {benchmarks.team_insights?.length > 0 && (
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold font-['Manrope']">Team Insights</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {benchmarks.team_insights.map((insight, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                            <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                            {insight}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center">
                  <Button onClick={fetchBenchmarks} disabled={benchmarkLoading} data-testid="load-benchmarks-btn">
                    <Award className="w-4 h-4 mr-2" /> Analyze Performance
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ManagementCopilotPage;
